import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";

interface RetirementResults {
  totalSaved: number;
  inheritanceLeft: number;
  monthlyRetirementIncome: number;
  monthlyInvestment: number;
  retirementGoal: number;
  monthsToRetirement: number;
  annualReturnRate: number;
  monthlyRetirementExpense: number;
  monthlyIncome: number;
  maxMonthlySpending: number;
}

export const RetirementResponse = ({
  results,
}: {
  results: RetirementResults;
}) => {
  const monthlyRate = Math.pow(1 + results.annualReturnRate, 1 / 12) - 1;

  const requiredMonthlyInvestment =
    (results.retirementGoal * monthlyRate) /
    (Math.pow(1 + monthlyRate, results.monthsToRetirement) - 1);

  const requiredPercentage =
    (requiredMonthlyInvestment / results.monthlyIncome) * 100;

  const isGoalAchievable = results.totalSaved >= results.retirementGoal;

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado</MainCardTitle>
      <div className="bg-gray-100 p-4 rounded-lg mb-4">
        {isGoalAchievable ? (
          <p className="text-green-600 mb-2">
            Parabéns! Você já atingiu sua meta de aposentadoria com os
            investimentos atuais.
          </p>
        ) : (
          <>
            <p className="text-gray-600 mb-2">
              Para atingir sua meta, você precisa investir R$
              {normalSanatizerBRL(requiredMonthlyInvestment)} por mês para se
              aposentar com R$
              {normalSanatizerBRL(results.retirementGoal)}.
            </p>
            <p className="text-gray-600 mb-2">
              Isso representa {requiredPercentage.toFixed(2)}% da sua renda
              mensal
              {requiredPercentage > 100 &&
                ". Será necessário aumentar sua renda mensal para alcançar a meta"}
              .
            </p>
          </>
        )}
      </div>
      <div className="bg-gray-100 p-4 rounded-lg mb-4">
        {results.inheritanceLeft > 0 ? (
          <>
            <p className="text-gray-600 mb-2">
              Você conseguirá deixar uma herança para seus filhos.
            </p>
            <p className="text-green-600 font-bold">
              Você poderá gastar até R$
              {normalSanatizerBRL(results.monthlyRetirementExpense)} por mês e
              ainda deixará uma herança de R$
              {normalSanatizerBRL(results.inheritanceLeft)}.
            </p>
          </>
        ) : (
          <p className="text-gray-600 mb-2">
            Infelizmente, com seus gastos atuais, você não deixará nada de
            herança.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-3 rounded border flex flex-col justify-center">
          <h2 className="text-sm text-center font-semibold m-0">
            Você se aposentará com
          </h2>
          <p className="text-lg text-center font-bold text-gray-700 m-0">
            R${normalSanatizerBRL(results.totalSaved)}
          </p>
          <p className="text-xs text-center text-gray-500 m-0">
            Aportando R${normalSanatizerBRL(results.monthlyInvestment)} por{" "}
            {results.monthsToRetirement / 12} anos.
          </p>
        </div>

        <div className="bg-white p-3 rounded border flex flex-col justify-center">
          <h2 className="text-sm text-center font-semibold m-0">
            Deixará de herança
          </h2>
          <p className="text-lg text-center font-bold text-red-600 m-0">
            R${normalSanatizerBRL(Math.max(0, results.inheritanceLeft))}
          </p>
          <p className="text-xs text-center text-gray-500 m-0">
            Baseado numa expectiva média de 72 anos.
          </p>
        </div>

        <div className="bg-white p-3 rounded border flex flex-col justify-center">
          <h2 className="text-sm text-center font-semibold m-0">
            Poderá gastar por mês
          </h2>
          <p className="text-lg text-center font-bold text-black m-0">
            R${normalSanatizerBRL(results.monthlyRetirementIncome)}
          </p>
          <p className="text-xs text-center text-gray-500 m-0">
            Para seu dinheiro nunca acabar.
          </p>
        </div>

        <div className="bg-white p-3 rounded border flex flex-col justify-center">
          <h2 className="text-sm text-center font-semibold m-0">
            {results.totalSaved > results.retirementGoal
              ? "Você passou da sua"
              : "Falta para sua"}{" "}
            meta
          </h2>
          {results.totalSaved > results.retirementGoal ? (
            <>
              <p className="text-lg text-center font-bold text-green-600 m-0">
                R$
                {normalSanatizerBRL(
                  results.totalSaved - results.retirementGoal
                )}
              </p>
              <p className="text-xs text-center text-green-600 m-0">
                Parabéns!
              </p>
            </>
          ) : (
            <>
              <p className="text-lg text-center font-bold text-red-600 m-0">
                R$
                {normalSanatizerBRL(
                  Math.max(0, results.retirementGoal - results.totalSaved)
                )}
              </p>
              <p className="text-xs text-center text-gray-500 m-0">Que pena!</p>
            </>
          )}
        </div>
      </div>
    </MainCard>
  );
};
